import {
  useMutation,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { breakfastEventsPrefix } from "../queryKeys";
import type { BreakfastEventWithAttendees, PotluckItem } from "../types";

// ── Claim / release a potluck item (atomic CAS) ─────────────────────────
//
// Previously a direct `UPDATE potluck_items SET claimed_by = <me>` with
// RLS alone guarding against "stealing". RLS is enforced at-the-row, but
// PostgREST returns `{ error: null }` whether the filter matched 1 row
// or 0 — so two users racing for the same item could both see "success"
// and the loser's optimistic checkbox would never roll back. Silent data
// loss on the read-your-writes side.
//
// Now routed through the Phase 26 RPC `claim_potluck_item`, which wraps
// the UPDATE in a single `WHERE claimed_by IS NULL` compare-and-swap
// under a row-level lock and `RAISE EXCEPTION` on a zero-row hit. That
// gives us a concrete server-side error to branch on:
//
//   SQLSTATE  function path        user-visible reason
//   ────────  ───────────────────  ─────────────────────────────────────
//   55P03     claim, lost the race "Someone else just claimed this item."
//   02000     release, not owner   "This item isn't yours to release."
//   42501     not an attendee      "You're not in this event."
//   22023     null p_item_id       (defensive — should never happen)
//
// Anything that falls through the map gets the raw PG message — good
// enough for rare unknown cases and still actionable in console logs.
//
// The optimistic-update pipeline below is unchanged; we just upgrade the
// write path and layer a specific toast onto `onError`.
//
// NOTE on the query key: `useBreakfastEvents` registers its query under
//   ["breakfastEvents", userId, startDate, endDate]
// so we MUST filter by the `breakfastEventsPrefix` (the default
// `exact: false` behaviour) rather than looking it up by that literal key,
// otherwise nothing matches and the optimistic update silently no-ops.

type ClaimVars = { eventId: string; itemId: string; release?: boolean };
type ClaimCtx = {
  previous: Array<[QueryKey, BreakfastEventWithAttendees[] | undefined]>;
};

export function useClaimPotluckItem() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<void, Error, ClaimVars, ClaimCtx>({
    mutationKey: ["claimPotluckItem"],
    mutationFn: async ({ itemId, release = false }) => {
      if (!itemId) throw new Error("Item ID is required");

      // Supabase's RPC wrapper returns `{ data, error }`. `error` is a
      // PostgrestError with `.code` (SQLSTATE), `.message`, `.details`,
      // `.hint`. We re-throw an `Error` whose `.message` is the PG
      // `RAISE EXCEPTION` text, and we stash the SQLSTATE on a
      // `(err as any).code` so `onError` can map it to a friendlier
      // string without re-parsing the message.
      const { error } = await supabase.rpc("claim_potluck_item", {
        p_item_id: itemId,
        p_release: release,
      });

      if (error) {
        const wrapped: Error & { code?: string } = Object.assign(
          new Error(error.message || "Could not update potluck item."),
          { code: (error as { code?: string }).code },
        );
        throw wrapped;
      }
    },

    // 1. Cancel in-flight refetches so they can't overwrite our optimistic
    //    state. 2. Snapshot every matching cache entry (prefix match). 3.
    //    Patch every matching cache entry with the new claim state, also
    //    synthesizing the `claimer` embed so the initials badge appears
    //    instantly. 4. Return the snapshot to `onError` for rollback.
    onMutate: async ({ eventId, itemId, release }) => {
      await queryClient.cancelQueries({ queryKey: breakfastEventsPrefix });

      const previous = queryClient.getQueriesData<BreakfastEventWithAttendees[]>({
        queryKey: breakfastEventsPrefix,
      });

      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id ?? null;

      queryClient.setQueriesData<BreakfastEventWithAttendees[] | undefined>(
        { queryKey: breakfastEventsPrefix },
        (old) => {
          if (!old) return old;
          return old.map((ev) => {
            if (ev.id !== eventId) return ev;

            // Derive the optimistic claimer profile from the event's own
            // attendees list — that's the exact same shape the server will
            // return via the `claimer:profiles(...)` embed, so the UI badge
            // renders identically before and after reconciliation.
            const me = ev.attendees?.find((a) => a.user_id === userId);
            const optimisticClaimer = me?.profiles
              ? {
                  first_name: me.profiles.first_name,
                  last_name: me.profiles.last_name,
                  email: me.profiles.email,
                }
              : null;

            const items = Array.isArray(ev.potluck_items) ? ev.potluck_items : [];
            return {
              ...ev,
              potluck_items: items.map((it) => {
                if (it.id !== itemId) return it;
                if (release) {
                  return { ...it, claimed_by: null, claimer: null };
                }
                return {
                  ...it,
                  claimed_by: userId ?? it.claimed_by,
                  claimer: optimisticClaimer ?? it.claimer ?? null,
                };
              }),
            };
          });
        },
      );

      return { previous };
    },

    // Rollback path.
    //
    // Two responsibilities:
    //   (a) Restore the per-key snapshot so the checkbox snaps back to its
    //       pre-click state. We write to each cache slot individually
    //       rather than calling `setQueriesData` over the prefix, because
    //       prefix-writes broadcast the same value to every matching key
    //       — fine for identical payloads but subtly wrong when the cache
    //       holds distinct paginated/date-windowed slices.
    //   (b) Raise a user-visible toast. The generic "Could not update
    //       event" copy we used for other mutations isn't actionable for
    //       race-loss — the specific "Someone else just claimed this
    //       item" message is what tells the user why their click was
    //       reverted, and it's the only reason they need to retry
    //       differently (pick a different item) rather than retry the
    //       same action (which would just lose the race again).
    onError: (err, vars, context) => {
      if (context) {
        context.previous.forEach(([key, snap]) => {
          queryClient.setQueryData(key, snap);
        });
      }

      const sqlState = (err as { code?: string })?.code;
      let title = vars.release ? "Could not release item" : "Could not claim item";
      let description = err?.message || "Please try again.";

      switch (sqlState) {
        case "55P03": // lock_not_available → lost the CAS race
          title = "Someone beat you to it";
          description = "Someone else just claimed this item. Pick another one!";
          break;
        case "02000": // no_data_found → release path, user isn't the owner
          description = "This item isn't yours to release.";
          break;
        case "42501": // insufficient_privilege → not an attendee
          description = "You're not listed as an attendee for this event.";
          break;
      }

      toast({ title, description, variant: "destructive" });
    },

    // Source of truth. Runs after both success and error so the cache
    // always converges to whatever the DB actually says — which in the
    // race-loss case means the avatar of the user who *did* win the CAS
    // will replace our rolled-back checkbox, rather than the whole row
    // reverting and then flickering into the winner's state on the next
    // realtime push.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: breakfastEventsPrefix,
        refetchType: "all",
      });
    },
  });
}

/**
 * Direct INSERT into potluck_items — used by the event detail drawer to
 * add items after event creation.
 */
export function useAddPotluckItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { eventId: string; itemName: string }) => {
      const name = params.itemName.trim();
      if (!name) throw new Error("Item name is required");

      const { data, error } = await supabase
        .from("potluck_items")
        .insert({ event_id: params.eventId, item_name: name })
        .select()
        .single();

      if (error) throw error;
      return data as PotluckItem;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: breakfastEventsPrefix }),
  });
}

export function useRemovePotluckItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { itemId: string }) => {
      const { error } = await supabase
        .from("potluck_items")
        .delete()
        .eq("id", params.itemId);
      if (error) throw error;
      return true;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: breakfastEventsPrefix }),
  });
}
